import { Observable, forkJoin, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';

/**
 * Runs a company tab's own save(s) to completion, then the shared keyword save.
 *
 * Emits `[...mainResults, keywordsResult]`, the same positional shape
 * `forkJoin([...mainCalls, keywordsCall])` produced, so callers destructure
 * exactly as they did before.
 *
 * The tabs used to fire both in parallel under one forkJoin, which behaves badly
 * the moment the tab's own save is rejected: forkJoin errors on the first failure
 * and unsubscribes from its siblings, so the keyword request either
 *
 *   - had already committed, leaving the tab reporting "Failed to save" having
 *     silently written the keywords, or
 *   - was aborted mid-body, which the server sees as a truncated request and logs
 *     as an unhandled `BadHttpRequestException: Unexpected end of request content`.
 *
 * Both were observed in production from a single rejected employees save. Running
 * them in sequence removes both: a rejected tab save never sends the keyword
 * request at all, so "nothing saved" is the truth rather than a guess.
 */
export function saveTabThenKeywords(
  mainCalls: Observable<unknown>[],
  keywordsCall: Observable<unknown>
): Observable<any[]> {
  // forkJoin([]) completes without ever emitting, which would swallow the save.
  const main$: Observable<unknown[]> = mainCalls.length > 0 ? forkJoin(mainCalls) : of([]);

  return main$.pipe(
    concatMap(mainResults =>
      keywordsCall.pipe(map(keywordsResult => [...mainResults, keywordsResult]))
    )
  );
}
